import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Paper,
  IconButton,
  Autocomplete,
  Avatar,
  Divider,
  Stack,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import axios from "axios";
import baseURL from "../../config";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

// ⚠️ IMPORTANT: Adjust the path below if your worker file is elsewhere.
const BgRemoverWorker = typeof Worker !== 'undefined'
  ? new Worker(new URL('../../Crewing_Agent/workers/bg-remover.worker.js', import.meta.url))
  : null;


const UploadCV = () => {
  const [cvEntries, setCvEntries] = useState([
    { name: "", rank: "", birthdate: "", nationality: "", file: null, profilePic: null, progress: 0, preview: null },
  ]);
  const [ranks, setRanks] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingRanks, setLoadingRanks] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [processingImages, setProcessingImages] = useState({});

  // Fetch ranks
  useEffect(() => {
    const fetchRanks = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setMessage("Authentication token not found. Please log in again.");
          return;
        }
        const res = await axios.get(`${baseURL}/api/rankData`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRanks(res.data.data || []);
      } catch (error) {
        console.error("Error fetching ranks:", error);
        setMessage("Error loading ranks. Please try again.");
      } finally {
        setLoadingRanks(false);
      }
    };
    fetchRanks();
  }, []);

  // Fetch countries for nationality
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get("https://restcountries.com/v3.1/all?fields=name");
        // Map to an array of country common names and sort alphabetically
        const countryList = res.data
          .map(c => c.name?.common)
          .filter(Boolean) // remove any undefined/null
          .sort((a, b) => a.localeCompare(b));
        setCountries(countryList);
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);


  const handleChange = (index, field, value) => {
    const updated = [...cvEntries];
    updated[index][field] = value;
    setCvEntries(updated);
  };

  const handleFileChange = (index, file) => {
    const updated = [...cvEntries];
    updated[index].file = file;
    setCvEntries(updated);
  };

  // ----------------------------------------------------------------------
  // Worker Logic Setup
  // ----------------------------------------------------------------------

  // Unified state update function to handle progress, success, and error
  const updateCvEntry = useCallback((index, updates) => {
    setCvEntries(prev => {
      const newEntries = [...prev];
      if (newEntries[index]) {
        newEntries[index] = { ...newEntries[index], ...updates };
      }
      return newEntries;
    });
  }, []);

  // Effect to set up and clean up the worker listener
  useEffect(() => {
    if (!BgRemoverWorker) return;

    const workerListener = (event) => {
      const { type, index, data, message, fileName } = event.data;

      switch (type) {
        case 'progress':
          // Update progress state without blocking UI
          updateCvEntry(index, { progress: data });
          break;
        case 'success':
          // Clean up previous URL if it exists
          const prevEntry = cvEntries[index];
          if (prevEntry && prevEntry.preview) {
            URL.revokeObjectURL(prevEntry.preview);
          }

          const processedFile = new File(
            [data],
            fileName,
            { type: "image/png" }
          );
          const previewURL = URL.createObjectURL(data);

          // Update final state on success
          updateCvEntry(index, {
            profilePic: processedFile,
            preview: previewURL,
            progress: 100
          });
          setProcessingImages(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          setMessage("✅ Background removed successfully!");
          break;
        case 'error':
          // Handle error state
          updateCvEntry(index, { progress: 0, preview: null });
          setProcessingImages(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          setMessage(`❌ Error for entry ${index + 1}: ${message}`);
          break;
        default:
          break;
      }
    };

    BgRemoverWorker.onmessage = workerListener;

    // Clean up function to revoke object URLs and remove listener on unmount
    return () => {
      // Clean up previews to prevent memory leaks
      cvEntries.forEach(entry => {
        if (entry.preview) {
          URL.revokeObjectURL(entry.preview);
        }
      });
      // It is usually better to keep the worker running for re-use, 
      // but if you prefer to terminate: BgRemoverWorker.terminate(); 
    };
  }, [updateCvEntry, cvEntries]);

  // ----------------------------------------------------------------------
  // Image Resizing (Still on Main Thread, but FAST)
  // ----------------------------------------------------------------------

  const resizeImage = (file, maxWidth = 600, maxHeight = 600) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              // Resolve with a new File object
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.85
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // ----------------------------------------------------------------------
  // New handleProfilePicChange using the Worker
  // ----------------------------------------------------------------------

  const handleProfilePicChange = async (index, file) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage("❌ Image is too large. Please select an image smaller than 5MB.");
      return;
    }

    if (!BgRemoverWorker) {
      setMessage("❌ Background removal service is not available in this browser.");
      return;
    }

    setProcessingImages(prev => ({ ...prev, [index]: true }));
    updateCvEntry(index, { progress: 0, preview: null }); // Reset state

    try {
      // 1. Resize the image quickly on the main thread (minimal block)
      const resizedFile = await resizeImage(file, 600, 600);

      // 2. Send the resized image to the Web Worker for heavy processing
      BgRemoverWorker.postMessage({
        file: resizedFile,
        fileName: file.name,
        index
      });

      setMessage("Processing image in background...");

    } catch (error) {
      console.error("Error during pre-processing:", error);
      setProcessingImages(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      setMessage("❌ Error preparing image. Try a different file.");
    }
  };

  const handleAddEntry = () => {
    setCvEntries([
      ...cvEntries,
      { name: "", rank: "", birthdate: "", nationality: "", file: null, profilePic: null, progress: 0, preview: null },
    ]);
  };

  const handleRemoveEntry = (index) => {
    // Revoke object URL to prevent memory leaks
    if (cvEntries[index].preview) {
      URL.revokeObjectURL(cvEntries[index].preview);
    }

    setCvEntries(cvEntries.filter((_, i) => i !== index));
    setProcessingImages(prev => { // Also clear processing state if entry removed
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (Object.keys(processingImages).length > 0) {
      setMessage("❌ Please wait for all images to finish processing before uploading.");
      return;
    }

    const formData = new FormData();
    let hasFilesToUpload = false;

    cvEntries.forEach((entry) => {
      if (entry.file) {
        formData.append("cvs", entry.file);
        formData.append("name[]", entry.name);
        formData.append("rank[]", entry.rank);
        formData.append("birthdate[]", entry.birthdate);
        formData.append("nationality[]", entry.nationality);
        hasFilesToUpload = true;
      }
      // Always append a value for profilePicture if we are processing the entry
      formData.append("profilePictures", entry.profilePic || "");
    });

    if (!hasFilesToUpload) {
      setMessage("⚠️ Please select at least one CV file to upload.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMessage("Authentication token not found. Please log in again.");
        return;
      }
      setUploading(true);
      await axios.post(`${baseURL}/api/crewCVuploadByCrewingAgentRoute`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setMessage("✅ CVs uploaded successfully!");
      // Reset after successful upload
      setCvEntries([{ name: "", rank: "", birthdate: "", nationality: "", file: null, profilePic: null, progress: 0, preview: null }]);
      // You may consider removing the reload if your application handles state globally better
      // setTimeout(() => { window.location.reload(); }, 2000); 
    } catch (error) {
      console.error("Error uploading CVs:", error);
      setMessage("❌ Error uploading CVs. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 5, px: 2 }}>
      <Paper elevation={6} sx={{ p: 4, borderRadius: 4, background: "linear-gradient(180deg, #ffffff, #f8f9fa)" }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", textAlign: "center", mb: 4, color: "#2b2d42" }}>
          📄 Upload Crew CVs
        </Typography>

        {cvEntries.map((entry, index) => (
          <Paper
            key={index}
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              backgroundColor: "#ffffff",
              boxShadow: "0px 3px 6px rgba(0,0,0,0.05)",
              "&:hover": { boxShadow: "0px 6px 12px rgba(0,0,0,0.1)" },
            }}
          >
            <Stack spacing={2}>
              {/* Crew Name */}
              <TextField
                label="Crew Name"
                value={entry.name}
                onChange={(e) => handleChange(index, "name", e.target.value)}
                fullWidth
              />

              {/* Birthdate */}
              <TextField
                label="Birthdate"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={entry.birthdate}
                onChange={(e) => handleChange(index, "birthdate", e.target.value)}
                fullWidth
              />

              {/* Nationality */}
              <Autocomplete
                options={countries}
                loading={loadingCountries}
                value={entry.nationality || ""}
                onChange={(event, newValue) => handleChange(index, "nationality", newValue || "")}
                renderInput={(params) => <TextField {...params} label="Nationality" placeholder="Select nationality" />}
              />

              {/* Rank */}
              <Autocomplete
                options={ranks}
                getOptionLabel={(option) => option.rankname}
                loading={loadingRanks}
                isOptionEqualToValue={(option, value) => option.rankname === value.rankname}
                renderOption={(props, option, { inputValue }) => {
                  const matches = match(option.rankname, inputValue);
                  const parts = parse(option.rankname, matches);
                  return (
                    <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
                      <Avatar sx={{ bgcolor: "#1976d2", fontSize: 14, width: 32, height: 32 }}>
                        {option.rankname[0]}
                      </Avatar>
                      <Box>
                        <Typography fontWeight="bold">
                          {parts.map((part, idx) =>
                            part.highlight ? <span key={idx} style={{ color: "#d32f2f" }}>{part.text}</span> : part.text
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description || "No description"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => <TextField {...params} label="Rank" placeholder="Search or select a rank" />}
                value={ranks.find((r) => r.rankname === entry.rank) || null}
                onChange={(event, newValue) => handleChange(index, "rank", newValue ? newValue.rankname : "")}
                disableClearable
              />

              {/* CV File Upload */}
              <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                {entry.file ? entry.file.name : "Select CV File"}
                <input type="file" accept=".pdf,.doc,.docx" hidden onChange={(e) => handleFileChange(index, e.target.files[0])} />
              </Button>

              {/* Profile Picture Upload + Preview (Worker Integration) */}
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={processingImages[index] ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={processingImages[index]}
                  fullWidth
                >
                  {processingImages[index]
                    ? `Processing (${entry.progress}%)`
                    : entry.profilePic
                      ? entry.profilePic.name
                      : "Select Profile Picture (Max 5MB)"}
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    hidden
                    onChange={(e) => handleProfilePicChange(index, e.target.files[0])}
                    disabled={processingImages[index]}
                  />
                </Button>

                {/* Processing Message with Progress */}
                {processingImages[index] && (
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <CircularProgress
                      variant="determinate"
                      value={entry.progress}
                      size={40}
                      sx={{ mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#1976d2",
                        fontWeight: "medium",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1
                      }}
                    >
                      Removing background... ({entry.progress}%)
                    </Typography>

                  </Box>
                )}



              </Box>

              {/* Delete Button */}
              {cvEntries.length > 0 && (
                <IconButton color="error" onClick={() => handleRemoveEntry(index)} sx={{ alignSelf: "flex-end" }}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          </Paper>
        ))}

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" color="primary" onClick={handleAddEntry} startIcon={<AddCircleOutlineIcon />}>
            Add Another Crew CV
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={uploading || Object.keys(processingImages).length > 0} // Disable if processing
            onClick={handleSubmit}
            sx={{ px: 4, fontWeight: "bold" }}
          >
            {uploading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Upload All"}
          </Button>
        </Stack>

        {message && (
          <Typography sx={{ mt: 3, textAlign: "center", fontWeight: "bold", color: message.startsWith("✅") ? "green" : "red" }}>
            {message}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default UploadCV;